import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { MyTimesheetRoleFilter } from "my-timesheet/components";
import {
  useMyTimesheetPeriodSelector,
  useMyTimesheetWorktimeSummaryData,
} from "my-timesheet/hooks";
import { Period } from "my-timesheet/types/period";
import { OcCard, OcDataTable, OcIconForState } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcRoleFilters,
} from "oc/hooks";
import { RoleFilterInput } from "oc/types";

type MyTimesheetWorktimeSummaryMonthlyProps = {
  periodStart: string | Date;
  periodEnd: string | Date;
  period: Period;
  product?: string;
  role: string;
  handlePeriodChange: (period: Period) => void;
  isMobile: boolean;
};

export const MyTimesheetWorktimeSummaryMonthly = ({
  periodStart,
  periodEnd,
  period,
  product = "MyTimesheet",
  role,
  handlePeriodChange,
  isMobile,
}: MyTimesheetWorktimeSummaryMonthlyProps) => {
  const { t } = useTranslation();
  let { contractCode: contract } = useOcCurrentUserState();

  const { OcButtonGroup } = useMyTimesheetPeriodSelector({
    period: period as Period,
    handlePeriodChange,
  });

  let tableId = "myTimesheetEmployeeRequestsData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "employeeName",
    pageSize: 5,
  });

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract: contract,
    product: product,
    role: role,
  });

  const {
    columnDefsMobile,
    columnDefsDesktop,
    data,
    loading,
    error,
    runQuery,
    queryData,
  } = useMyTimesheetWorktimeSummaryData({
    periodStart:
      typeof periodStart === "string" ? periodStart : periodStart.toISOString(),
    periodEnd:
      typeof periodEnd === "string" ? periodEnd : periodEnd.toISOString(),
    period,
    tableData: tableData,
    roleFilters: roleFilters as RoleFilterInput,
    role: role,
  });

  const transformedData = {
    select: isMobile ? "row" : "none",
    id: isMobile ? columnDefsMobile?.id : columnDefsDesktop?.id,
    columns: (isMobile
      ? columnDefsMobile?.columns
      : columnDefsDesktop?.columns
    )?.map((col) => ({
      id: col?.id,
      label: col?.label,
      align: col?.align,
      width: col?.width,
      type: col?.type,
      sort: col?.sort,
      search: col?.search,
    })),
  };

  const filteredData = data?.map(({ employeeCode, ...rest }) => ({
    ...rest,
    employeeCode,
  }));

  const requestsData = {
    rows: filteredData,
    total: queryData?.total,
    page: tableData?.page,
    pageSize: tableData?.pageSize,
    searchField: tableData?.searchField,
    searchText: tableData?.searchText,
  };

  const handleTableDataChange = (updatedData: typeof initialTableData) => {
    setTableData(updatedData);
  };

  useEffect(() => {
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodStart,
    periodEnd,
    period,
    tableData.page,
    tableData.pageSize,
    tableData.sortField,
    tableData.sortDirection,
  ]);

  useEffect(() => {
    if (tableData.searchText?.length >= 3) {
      setTableData({ ...tableData, searched: true });
      runQuery();
    } else if (tableData.searched && tableData.searchText?.length < 3) {
      setTableData({ ...tableData, searched: false });
      runQuery();
    }
  }, [tableData.searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTableDef = {
    id: columnDefsDesktop?.id,
    columns: columnDefsDesktop?.columns?.filter(
      (column) =>
        !columnDefsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(
    undefined,
  );

  const onSelectRow = (row: { _id?: string }) => {
    let selectedId =
      selectedRowId === row?._id ? undefined : (row?._id ?? undefined);
    setSelectedRowId(selectedId);
  };

  return (
    <>
      <OcCard
        label={t("MY_TIMESHEET_WORKTIME_SUMMARY")}
        loading={loading}
        error={!!error}
      >
        <Box height="16px" />
        <Box display="flex" flexDirection="row" justifyContent="center">
          <OcButtonGroup />
        </Box>
        <Box height="32px" />
        {requestsData?.total === 0 ? (
          <OcIconForState
            data={data}
            loading={loading}
            isMobile={isMobile}
            error={!!error}
            module="MyTimesheet"
          />
        ) : (
          <>
            <MyTimesheetRoleFilter
              data={
                queryData
                  ? {
                      __typename: "MonthlyUsersSummaryResponse",
                      ...queryData,
                    }
                  : undefined
              }
              product={product}
              role={role}
              contract={contract}
              resetRoleFilters={resetRoleFilters}
              roleFilters={roleFilters}
              prevRoleFilters={prevRoleFilters}
              setPrevRoleFilters={setPrevRoleFilters}
              handleAddFilter={handleAddFilter}
              handleRemoveFilter={handleRemoveFilter}
              buttonGroup={false}
              runQuery={runQuery}
              refetch={runQuery}
              setOnlyRequested={() => {}}
            />
            <Box height="32px" />
            <OcDataTable
              data={requestsData}
              loading={loading}
              tableDef={transformedData}
              tableData={tableData}
              setTableData={(updatedData) => handleTableDataChange(updatedData)}
              pagination={true}
              onSelectRow={onSelectRow}
              selectedRowId={selectedRowId}
              filteredTableDef={filteredTableDef}
            />
          </>
        )}
      </OcCard>
    </>
  );
};

export default MyTimesheetWorktimeSummaryMonthly;
